import { useCallback, useEffect, useRef, useState } from 'react';
import { ScrollView } from 'react-native';
import type { NativeSyntheticEvent, NativeScrollEvent } from 'react-native';
import {
  Flex,
  RemoteImage,
  Typography,
  IconAdd,
  ColorValues,
  Carousel,
} from '@banhala/ui-kit';
import type { RollingBannerListProps, RollingBannerItem } from './types';
import type { AnyObject } from '../../../../types';
import { bridge, isWeb } from '../../../../utils';
import { useLogClickBanner } from './hooks/useLogClickBanner';
import { useSharedValue } from 'react-native-reanimated';
import {
  useGuardedScreenWidth,
  useOpenDeeplink,
  useForegroundInterval,
  useIsForegroundNow,
} from '../../../../hooks';

const AUTO_SCROLL_INTERVAL = 5000;

export const RollingBannerList = ({
  entity,
  body,
  logging,
  logDisabled,
  isVisible = true,
}: RollingBannerListProps) => {
  const isForegroundNow = useIsForegroundNow();
  const screenWidth = useGuardedScreenWidth('RollingBannerList');
  const { openDeeplink } = useOpenDeeplink();
  const { logClickBannerAll, logClickBannerItem } = useLogClickBanner(logging);
  const ratio = body.item.ratio;
  const bannerHeight = screenWidth / ratio;

  const scrollRef = useRef<ScrollView>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const currentIndexRef = useRef(0);
  const isDraggingRef = useRef(false);
  const displayedIndicesRef = useRef<Set<number>>(new Set());

  const itemCount = entity.length;
  const isInfinite = itemCount > 1;
  const showIndicator = itemCount > 1;
  const progress = useSharedValue<number>(0);

  // 무한 스크롤: [마지막, ...원본, 첫번째]
  const infiniteItems = isInfinite
    ? [entity[itemCount - 1], ...entity, entity[0]]
    : entity;

  // 무한 스크롤 시 실제 첫 아이템은 index 1
  useEffect(() => {
    if (isInfinite) {
      scrollRef.current?.scrollTo({ x: screenWidth, animated: false });
    }
  }, [isInfinite, screenWidth]);

  // entity 변경 시 로깅 초기화 (iOS: DisplayTracker.refresh → sentIDs.removeAll)
  useEffect(() => {
    displayedIndicesRef.current = new Set();
  }, [entity]);

  // 현재 보이는 배너의 디스플레이 로깅 (iOS: DisplayTracker → VIEW_BANNER, 아이템별)
  useEffect(() => {
    if (itemCount === 0) return;

    if (!isForegroundNow) {
      return;
    }
    const bannerItem = entity[currentIndex];
    if (!bannerItem) return;

    if (displayedIndicesRef.current.has(currentIndex)) return;
    displayedIndicesRef.current.add(currentIndex);
    // iOS: mergedLogging(컴포넌트 + 아이템) 사용
    bridge.logAnalyticsEvent({
      name: 'VIEW_BANNER',
      analytics: {
        ...logging.analytics,
        ...bannerItem.logging.analytics,
      },
      ably_only_log: {
        ...(logging.ably_only_log as AnyObject),
        ...(bannerItem.logging.ably_only_log as AnyObject),
      },
    });
  }, [currentIndex, isForegroundNow, entity, itemCount, logging]);

  // 자동 롤링
  const autoScroll = useCallback(() => {
    if (isDraggingRef.current) return;
    const nextRealIndex = currentIndexRef.current + 1;
    // infiniteItems 기준 scrollIndex = realIndex + 1
    const scrollTarget = (nextRealIndex + 1) * screenWidth;
    scrollRef.current?.scrollTo({ x: scrollTarget, animated: true });

    if (nextRealIndex >= itemCount) {
      // 클론(첫번째)에 도달 → 애니메이션 후 실제 첫번째로 점프
      setTimeout(() => {
        scrollRef.current?.scrollTo({ x: screenWidth, animated: false });
      }, 400);
      currentIndexRef.current = 0;
      setCurrentIndex(0);
    } else {
      currentIndexRef.current = nextRealIndex;
      setCurrentIndex(nextRealIndex);
    }
  }, [itemCount, screenWidth]);

  useForegroundInterval(
    autoScroll,
    isVisible && itemCount > 1 && !logDisabled ? AUTO_SCROLL_INTERVAL : null,
    { immediate: false },
  );

  const handleScrollBeginDrag = useCallback(() => {
    isDraggingRef.current = true;
  }, []);

  const handleScrollEndDrag = useCallback(() => {
    isDraggingRef.current = false;
  }, []);

  const handleMomentumScrollEnd = useCallback(
    (e: NativeSyntheticEvent<NativeScrollEvent>) => {
      const scrollIndex = Math.round(
        e.nativeEvent.contentOffset.x / screenWidth,
      );

      if (isInfinite) {
        if (scrollIndex === 0) {
          // 클론(마지막) → 실제 마지막으로 점프
          scrollRef.current?.scrollTo({
            x: itemCount * screenWidth,
            animated: false,
          });
          currentIndexRef.current = itemCount - 1;
          setCurrentIndex(itemCount - 1);
          return;
        }
        if (scrollIndex === itemCount + 1) {
          // 클론(첫번째) → 실제 첫번째로 점프
          scrollRef.current?.scrollTo({ x: screenWidth, animated: false });
          currentIndexRef.current = 0;
          setCurrentIndex(0);
          return;
        }
        // infiniteItems에서 실제 인덱스는 scrollIndex - 1
        const realIndex = scrollIndex - 1;
        currentIndexRef.current = realIndex;
        setCurrentIndex(realIndex);
      } else {
        currentIndexRef.current = scrollIndex;
        setCurrentIndex(scrollIndex);
      }
    },
    [screenWidth, isInfinite, itemCount],
  );

  const handleClickBannerAll = useCallback(() => {
    if (body.item.deeplink) {
      logClickBannerAll();
      openDeeplink(body.item.deeplink);
    }
  }, [openDeeplink, body.item.deeplink, logClickBannerAll]);

  const handleBannerPress = useCallback(
    (item: RollingBannerItem) => {
      if (!item.item.deeplink) return;
      logClickBannerItem(item.logging);
      openDeeplink(item.item.deeplink);
    },
    [openDeeplink, logClickBannerItem],
  );

  if (itemCount === 0) return null;

  if (body.item.view_type === 'SLIDE_QUEUE') {
    if (!screenWidth) return null;

    return (
      <Flex position={'relative'} padding={{ top: isWeb ? 12 : 0 }}>
        <Carousel
          data={infiniteItems}
          onProgressChange={progress}
          mode="parallax"
          autoPlay={isVisible && !logDisabled}
          autoPlayInterval={3000}
          modeConfig={{
            parallaxScrollingScale: 0.9,
            parallaxScrollingOffset: 78,
            parallaxAdjacentItemScale: 0.76,
          }}
          renderItem={({ item: bannerItem, index }) => {
            return (
              <Flex.Center
                key={index}
                onPress={() => handleBannerPress(bannerItem)}
              >
                <RemoteImage
                  uri={bannerItem.item.image_url}
                  width={screenWidth * 0.91}
                  height={screenWidth * 0.91}
                  borderRadius={'xlarge'}
                />
              </Flex.Center>
            );
          }}
          vertical={false}
          width={screenWidth}
          height={screenWidth * 0.91}
        />
        <Flex.Row
          position={'absolute'}
          bottom={32}
          right={48}
          // borderWidth={1}
          // borderColor={'white'}
          borderRadius={20}
          backgroundColorValue={ColorValues.alpha_dark12}
          padding={{ horizontal: 6, vertical: 4 }}
          alignItems={'center'}
          justifyContent={'center'}
        >
          <Typography type={'body4'} color={'content_always_white'}>
            {currentIndex + 1}
          </Typography>
          <Flex
            width={1}
            height={8}
            backgroundColor={'white'}
            margin={{ horizontal: 4 }}
          />
          <Typography type={'body4'} rawColor={`#FFFFFF99`}>
            {itemCount}
          </Typography>
        </Flex.Row>
      </Flex>
    );
  }

  return (
    <Flex>
      <ScrollView
        ref={scrollRef}
        horizontal
        pagingEnabled
        showsHorizontalScrollIndicator={false}
        onScrollBeginDrag={handleScrollBeginDrag}
        onScrollEndDrag={handleScrollEndDrag}
        onMomentumScrollEnd={handleMomentumScrollEnd}
      >
        {infiniteItems.map((bannerItem, index) => (
          <Flex key={index} onPress={() => handleBannerPress(bannerItem)}>
            <RemoteImage
              uri={bannerItem.item.image_url}
              width={screenWidth}
              height={bannerHeight}
            />
          </Flex>
        ))}
      </ScrollView>
      {showIndicator && (
        <Flex.Row
          alignItems="center"
          position="absolute"
          bottom={16}
          right={16}
          backgroundColor="alpha_dark40"
          borderRadius={12}
          gap={2}
          padding={{
            vertical: 4,
            left: 10,
            right: body.item.deeplink ? 6 : 10,
          }}
          onPress={
            body.item.deeplink ? () => handleClickBannerAll() : undefined
          }
        >
          <Typography type="subtitle3" color="content_always_white">
            {currentIndex + 1}
          </Typography>
          <Typography type="body3" color="content_always_white">
            /
          </Typography>
          <Typography type="body3" color="content_always_white">
            {itemCount}
          </Typography>
          {body.item.deeplink && (
            <Flex margin={{ left: 4 }}>
              <IconAdd fill={ColorValues.white} width={16} height={16} />
            </Flex>
          )}
        </Flex.Row>
      )}
    </Flex>
  );
};
